import { useQuery } from '@tanstack/react-query'
import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon, BuildingOffice2Icon, CheckIcon } from '@heroicons/react/24/outline'
import { divecenterApi } from '@/api/divecenter'
import { useCenterStore } from '@/stores/center.store'

/**
 * Context switcher shown in the DiveCenter sidebar/topbar for staff
 * who belong to multiple centers.
 */
export function CenterSwitcher() {
  const { activeCenterId, activeCenterInfo, setActiveCenter } = useCenterStore()

  const { data } = useQuery({
    queryKey: ['my-centers'],
    queryFn: divecenterApi.myCenters,
    staleTime: 5 * 60 * 1000,
  })
  // Defensive: this is a shell component. Another route can populate the
  // ['my-centers'] cache key with a different shape (an object, not an array) —
  // never let that crash the whole app via `centers.find`. Coerce to an array.
  const centers = Array.isArray(data) ? data : []

  // If only one center (or none yet), no switcher needed
  if (centers.length <= 1) {
    const single = centers[0]
    if (single && !activeCenterId) {
      setActiveCenter(single)
    }
    if (!single) return null
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
        <BuildingOffice2Icon className="w-4 h-4 shrink-0 text-blue-500" />
        <span className="truncate">{single.name}</span>
      </div>
    )
  }

  // Ensure active center is one of the available ones
  const current = centers.find((c) => c.id === activeCenterId) ?? centers[0]
  if (current && current.id !== activeCenterId) {
    setActiveCenter(current)
  }

  return (
    <Menu as="div" className="relative w-full">
      <Menu.Button className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        {current?.logoUrl ? (
          <img src={current.logoUrl} alt={current.name} className="w-5 h-5 rounded-full object-cover shrink-0" />
        ) : (
          <BuildingOffice2Icon className="w-4 h-4 shrink-0 text-blue-500" />
        )}
        <span className="truncate flex-1 text-left">{current?.name ?? 'Seleziona centro'}</span>
        <ChevronDownIcon className="w-4 h-4 shrink-0 text-gray-400" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-50 mt-1 w-full min-w-[220px] origin-top-left rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 focus:outline-none">
          <div className="p-1">
            {centers.map((c) => (
              <Menu.Item key={c.id}>
                {({ active }) => (
                  <button
                    onClick={() => setActiveCenter(c)}
                    className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm ${
                      active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {c.logoUrl ? (
                      <img src={c.logoUrl} alt={c.name} className="w-5 h-5 rounded-full object-cover shrink-0" />
                    ) : (
                      <BuildingOffice2Icon className="w-4 h-4 shrink-0 text-blue-400" />
                    )}
                    <span className="flex-1 truncate text-left">
                      <span className="block font-medium">{c.name}</span>
                      {(c.city || c.country) && (
                        <span className="text-xs text-gray-400">
                          {[c.city, c.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </span>
                    {c.id === activeCenterId && (
                      <CheckIcon className="w-4 h-4 shrink-0 text-blue-500" />
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
